import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { createOrder, retryCheckout, ValidationError } from '../api/client'
import type { RestaurantMenu } from '../api/types'
import { useCart } from './useCart'
import { findUnavailableLines, describeUnavailable } from './unavailable'
import { saveCart } from './storage'
import { initialCartState } from './reducer'

export function useCheckout(slug: string) {
  const { lines, removeItem, clear } = useCart()
  const queryClient = useQueryClient()
  const [tableLabel, setTableLabel] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const inFlightRef = useRef(false)

  async function checkout() {
    // A plain ref, checked/set synchronously before any state update, is the
    // real double-submit guard. React can batch state updates inside an event
    // handler such that two rapid/synchronous checkout() calls could both
    // read stale `isSubmitting` before either setState commits — a ref
    // mutated synchronously has no such race.
    if (inFlightRef.current) return
    inFlightRef.current = true
    setIsSubmitting(true)
    setError(null)

    try {
      const payload = {
        lines: lines.map((l) => ({ menu_item_id: l.menuItemId, quantity: l.quantity })),
        ...(tableLabel.trim() ? { table_label: tableLabel.trim() } : {}),
      }
      const order = await createOrder(slug, payload)
      // Persisted immediately, before the checkout call below — so the order
      // id isn't lost if that call then fails. A diner who created an order
      // but never reached a redirect still has it recorded.
      sessionStorage.setItem(`qr-menu-order:${slug}`, order.order_id)
      const checkout = await retryCheckout(order.order_id)
      clear()
      // clear() only dispatches a reducer action — the cart's own sessionStorage
      // write happens in a useEffect that runs after this commits, which isn't
      // guaranteed to fire before the navigation below starts. Write the empty
      // cart synchronously here too, so a diner who backs out of the payment
      // page never finds their already-submitted items still in the cart.
      saveCart(slug, initialCartState)
      window.location.href = checkout.redirect_url
      // isSubmitting/inFlightRef intentionally never reset on success — navigation is happening.
    } catch (err) {
      if (err instanceof ValidationError) {
        const menu = queryClient.getQueryData<RestaurantMenu>(['menu', slug])
        const unavailable = findUnavailableLines(lines, menu)
        if (unavailable.length > 0) {
          unavailable.forEach((l) => removeItem(l.menuItemId))
          setError(describeUnavailable(unavailable))
        } else {
          // e.g. the minimum-order-total case, or restaurant-inactive case, or
          // the menu simply isn't cached — fall back to the raw backend message.
          setError(
            Object.values(err.body).flat().join(' ') ||
              'One or more items in your order could not be processed.',
          )
        }
      } else {
        setError('Something went wrong placing your order. Please try again.')
      }
      inFlightRef.current = false
      setIsSubmitting(false)
    }
  }

  return { tableLabel, setTableLabel, checkout, isSubmitting, error }
}
